/**
 * <pdp-lightbox>
 *
 * Full-screen image lightbox for the PDP gallery. Renders every curated
 * gallery image as a [data-role="slide"]; togges `--active` to swap which
 * slide is showing. Bottom thumb strip + on-stage arrows + keyboard nav.
 *
 * Public API:
 *   - .open(index)  — show the lightbox at a given slide index
 *   - .close()      — hide the lightbox
 *
 * The PDP section JS calls .open(idx) when the user clicks a hero slide
 * (cursor: zoom-in). Closing fires `pdp-lightbox:close` with the final
 * index so the underlying <pdp-gallery> can sync to whichever slide the
 * user was viewing when they closed.
 *
 * Selectors expected in the rendered markup:
 *   [data-role="slide"]            — each slide; carries data-index
 *   [data-role="lightbox-thumb"]   — clickable thumbnails
 *   [data-role="prev"|"next"]      — on-stage arrow buttons
 *   [data-role="close"]            — X button in the header
 *   [data-role="backdrop"]         — dim overlay; click to close
 */
(() => {
  if (customElements.get('pdp-lightbox')) return;

  class PdpLightbox extends HTMLElement {
    connectedCallback() {
      this.slides   = Array.from(this.querySelectorAll('[data-role="slide"]'));
      this.thumbs   = Array.from(this.querySelectorAll('[data-role="lightbox-thumb"]'));
      this.prevBtn  = this.querySelector('[data-role="prev"]');
      this.nextBtn  = this.querySelector('[data-role="next"]');
      this.closeBtn = this.querySelector('[data-role="close"]');
      this.backdrop = this.querySelector('[data-role="backdrop"]');

      if (this.slides.length === 0) return;

      this.current = 0;
      this._onKey = this._onKey.bind(this);
      this._lastFocus = null;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const idx = parseInt(thumb.dataset.index, 10);
          if (Number.isFinite(idx)) this.select(idx);
        });
      });

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.select(this.current - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.select(this.current + 1));
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      if (this.backdrop) this.backdrop.addEventListener('click', () => this.close());

      // Swipe support on touch devices — same gesture pattern as the slider.
      let startX = null;
      const stage = this.querySelector('[data-role="stage"]') || this;
      stage.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      stage.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) this.select(this.current + (dx < 0 ? 1 : -1));
        startX = null;
      });
    }

    open(idx = 0) {
      this._lastFocus = document.activeElement;
      this.select(idx);
      this.dataset.visible = 'true';
      this.setAttribute('aria-hidden', 'false');
      // Lock body scroll while the lightbox is open.
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKey);
      // Promote any lazy images we're about to show.
      this.slides.forEach((slide) => {
        const img = slide.querySelector('img');
        if (img && img.loading === 'lazy') img.loading = 'eager';
      });
      if (this.closeBtn) this.closeBtn.focus();
    }

    close() {
      this.dataset.visible = 'false';
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this._onKey);
      // Sync the underlying gallery to whichever slide we ended on so the
      // closed state reflects the user's last in-lightbox navigation.
      this.dispatchEvent(new CustomEvent('pdp-lightbox:close', {
        bubbles: true,
        detail: { index: this.current },
      }));
      // Restore focus to the trigger that opened us.
      if (this._lastFocus && typeof this._lastFocus.focus === 'function') {
        this._lastFocus.focus();
      }
    }

    select(idx) {
      const n = this.slides.length;
      if (n === 0) return;
      // Wrap around at both ends.
      idx = ((idx % n) + n) % n;
      this.current = idx;
      this.slides.forEach((s, i) => s.classList.toggle('salt-pdp-lightbox__slide--active', i === idx));
      this.thumbs.forEach((t, i) => {
        t.classList.toggle('salt-pdp-lightbox__thumb--selected', i === idx);
        if (i === idx) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      // Scroll the active thumb into view in case the strip overflows.
      const activeThumb = this.thumbs[idx];
      if (activeThumb && activeThumb.scrollIntoView) {
        activeThumb.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      }
    }

    _onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); this.close(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); this.select(this.current - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); this.select(this.current + 1); }
    }
  }

  customElements.define('pdp-lightbox', PdpLightbox);
})();
